import { db } from './firebase';
import {
  collection,
  doc,
  onSnapshot,
  setDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  Timestamp,
} from 'firebase/firestore';
import type { BoardCommentThread, BoardCommentReply, CommentMention } from '../types';

function commentsCol(dealId: string) {
  return collection(db, 'portalProjectBoards', dealId, 'comments');
}

function parseReply(raw: unknown): BoardCommentReply {
  const r = raw as Record<string, unknown>;
  const createdAtRaw = r.createdAt as { toDate?: () => Date } | undefined;
  return {
    userId: String(r.userId || ''),
    userName: String(r.userName || ''),
    content: String(r.content || ''),
    mentions: (r.mentions as CommentMention[]) || undefined,
    createdAt: createdAtRaw?.toDate?.() ?? new Date(),
  };
}

function fromFirestore(snap: { id: string; data: () => Record<string, unknown> }): BoardCommentThread {
  const d = snap.data();
  const createdAtRaw = d.createdAt as { toDate?: () => Date } | undefined;
  return {
    id: snap.id,
    nodeId: String(d.nodeId || snap.id),
    content: String(d.content || ''),
    authorUid: String(d.authorUid || ''),
    authorName: String(d.authorName || ''),
    authorKind: d.authorKind === 'client' ? 'client' : 'staff',
    mentions: (d.mentions as CommentMention[]) || undefined,
    replies: Array.isArray(d.replies) ? d.replies.map(parseReply) : [],
    createdAt: createdAtRaw?.toDate?.() ?? new Date(),
  };
}

export function subscribeBoardComments(
  dealId: string,
  callback: (threads: BoardCommentThread[]) => void,
): () => void {
  return onSnapshot(
    commentsCol(dealId),
    (snap) => callback(snap.docs.map((d) => fromFirestore({ id: d.id, data: () => d.data() }))),
    (err) => console.warn('[boardCommentService] subscribe failed', err),
  );
}

export async function createBoardThread(
  dealId: string,
  nodeId: string,
  input: {
    content: string;
    authorUid: string;
    authorName: string;
    authorKind: 'staff' | 'client';
    mentions?: CommentMention[];
    contentHtml?: string;
  },
): Promise<void> {
  const ref = doc(commentsCol(dealId), nodeId);
  const existing = await getDoc(ref);
  if (existing.exists()) {
    await addBoardReply(dealId, nodeId, {
      userId: input.authorUid,
      userName: input.authorName,
      content: input.content,
      mentions: input.mentions,
      contentHtml: input.contentHtml,
    });
    return;
  }
  await setDoc(ref, {
    nodeId,
    content: input.content,
    ...(input.contentHtml ? { contentHtml: input.contentHtml } : {}),
    authorUid: input.authorUid,
    authorName: input.authorName,
    authorKind: input.authorKind,
    mentions: input.mentions ?? [],
    replies: [],
    createdAt: Timestamp.now(),
  });
}

export async function addBoardReply(
  dealId: string,
  threadId: string,
  reply: {
    userId: string;
    userName: string;
    content: string;
    mentions?: CommentMention[];
    contentHtml?: string;
  },
): Promise<void> {
  const ref = doc(commentsCol(dealId), threadId);
  const snap = await getDoc(ref);
  if (!snap.exists()) return;
  const existing = (snap.data().replies as unknown[]) || [];
  await updateDoc(ref, {
    replies: [
      ...existing,
      {
        ...reply,
        mentions: reply.mentions ?? [],
        ...(reply.contentHtml ? { contentHtml: reply.contentHtml } : {}),
        createdAt: Timestamp.now(),
      },
    ],
  });
}

/** Post to a node: create thread if missing, otherwise append reply. */
export async function postBoardComment(
  dealId: string,
  nodeId: string,
  input: {
    content: string;
    authorUid: string;
    authorName: string;
    authorKind: 'staff' | 'client';
    mentions?: CommentMention[];
    contentHtml?: string;
  },
): Promise<void> {
  const ref = doc(commentsCol(dealId), nodeId);
  const snap = await getDoc(ref);
  if (!snap.exists()) {
    await createBoardThread(dealId, nodeId, input);
    return;
  }
  await addBoardReply(dealId, nodeId, {
    userId: input.authorUid,
    userName: input.authorName,
    content: input.content,
    mentions: input.mentions,
    contentHtml: input.contentHtml,
  });
}

export async function deleteBoardThread(dealId: string, threadId: string): Promise<void> {
  await deleteDoc(doc(commentsCol(dealId), threadId));
}

export async function deleteBoardCommentsForNode(dealId: string, nodeId: string): Promise<void> {
  const ref = doc(commentsCol(dealId), nodeId);
  const snap = await getDoc(ref);
  if (snap.exists()) await deleteDoc(ref);
}

export function boardCommentCount(thread: BoardCommentThread | undefined): number {
  if (!thread) return 0;
  return 1 + (thread.replies?.length || 0);
}
